<template>
    <div class="event-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && event.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="event"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <template v-if="editMode">
                                <button
                                    v-if="event.status !== CANCELLED"
                                    v-b-modal.confirm-cancel-event-modal
                                    class="btn btn-info btn-static waves-effect waves-light m-1"
                                >
                                    <i class="far fa-calendar-times" />
                                    Cancel
                                </button>
                            </template>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :item="event"
                            :sections="sections"
                            :edit-mode="editMode"
                            :featured-image="event.featuredImageId"
                            :campuses="event.campuses"
                            :campuses-error="campusesError"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-featured-image-change="onFeaturedImageChange"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>

        <skeleton-loader v-else />
        <users v-if="editMode" :event-id="$route.params.id" />
        <confirm-cancel-modal
            id="confirm-cancel-event-modal"
            title="Cancel event"
            @agree="onCancel"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required, numeric } from 'vuelidate/lib/validators';
import Users from '@/components/events/Users';
import AddEditForm from '@/components/events/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import ConfirmCancelModal from '@/components/elements/ConfirmationModal';

export default {
    page: {
        title: 'Manage event'
    },

    validations: {
        event: {
            name: {
                required
            },
            date: {
                required
            },
            price: {
                required,
                numeric
            },
            agudaPrice: {
                numeric
            },
            location: {
                required
            },
            description: {
                required
            },
            payWithStars: {
                required
            },
            campuses: {
                atLeastOneItem: val => val?.length > 0
            }
        }
    },

    components: {
        Users,
        AddEditForm,
        ItemInformation,
        ConfirmCancelModal
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit event' : 'Add event',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'events',
                    to: '/events'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            event: {
                price: 0,
                agudaPrice: null,
                payWithStars: false,
                campuses: [],
                content: []
            },
            informationKey: 0,
            serverErrors: []
        };
    },

    computed: {
        ...mapGetters({
            CANCELLED: 'events/CANCELLED'
        }),

        sections() {
            const sections = { featuredImage: true, campuses: true };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        options() {
            const options = {};

            return options;
        },

        campusesError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.event,
                this.serverErrors,
                'campuses'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.event = await this.getEvent(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addEvent: 'events/store',
            deleteEvent: 'events/destroy',
            getEvent: 'events/show',
            updateEvent: 'events/update',
            cancelEvent: 'events/cancel'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const date = this.$dayjs(this.event.date);
            const campuses = this.event.campuses.map(campus => campus.id);

            const data = {
                ...this.event,
                campuses,
                date
            };

            try {
                if (!this.editMode) {
                    const added = await this.addEvent(data);

                    this.$router.push(`/events/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateEvent({
                        id: this.id,
                        data
                    });

                    this.event.updatedAt = updatedAt;

                    this.$toaster('event updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteEvent(this.id);

                this.$router.push('/events');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onFeaturedImageChange(imageId) {
            this.event.featuredImageId = imageId;
        },

        onCampusesChange(campuses) {
            this.event.campuses = [...campuses];
            this.informationKey++;
        },

        async onCancel() {
            try {
                await this.cancelEvent(this.event.id);

                this.event.status = this.CANCELLED;

                this.$toaster('Event has been canceled.');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    }
};
</script>
